import React, {useState, useEffect} from "react";
import {
    VsiBubble,
    VsiH4,
    VsiH5,
    VsiLgText,
    VsiMedLabel,
    VsiMedText,
    VsiSmallText
} from "../../../../styled/App.styled";
import {useSearchParams} from "react-router-dom";
import { useVsiAppContext} from "../../../../stores/VsiApp";
import {getReport} from "../../../../utils/Verbs";
import { Pagination, PaginationItem, PaginationLink, Badge, Card, Spinner } from "reactstrap";
import {useTitleFromComponent} from "../../../../utils/hooks";
import DriveDiagram from "../../../../components/DriveDiagram";
import {formatPhysicalDiagramEnclosures} from "../../../../utils/Helpers";
import Grid from "../../../../components/Grid";
import themeStyles from "../../../../utils/themeStyles";
import {integerFormatter} from "../../../../utils/gridFormatters";

const firmwareDefs = [

    {
        "field": "drive_type",
        "headerName": "Drive Type",
        "enableRowGroup": true
    },
    {
        "field": "enclosure_name",
        "headerName": "Enclosure",
        "enableRowGroup": true
    },
    {
        "field": "revision",
        "headerName": "Firmware Revision",
        "enableRowGroup": true
    },
    {
        "field": "slot",
        "headerName": "Slot",
        "type": "numericColumn",
        "enableRowGroup": true
    },
    {
        "field": "speed",
        "type": "numericColumn",
        "filter": "agNumberColumnFilter",
        "headerName": "Speed",
        "enableRowGroup": true,
        "cellRenderer": integerFormatter
    },
    {
        "field": "size",
        "type": "numericColumn",
        "filter": "agNumberColumnFilter",
        "headerName": "Size",
        "cellRenderer": integerFormatter
    },
];

const LegendDiagram = ({label}) => (
    <div className={"d-inline-block"}>
        <div className={"d-flex flex-column"}>
            <div className={"px-2"}>
                <DriveDiagram drive={{drive_type: label}}/>
            </div>
            <VsiLgText className={"text-center"}>{label}</VsiLgText>
        </div>
    </div>
);

export default function PhysicalDiagram({ title}) {

    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true);
    const [activeLoop, setActiveLoop] = useState(0);
    const [activeLoopName, setActiveLoopName] = useState(null);
    const [diagrams, setDiagrams] = useState([]);
    const { theme, client, periodId, user, getAccessTokenSilently } = useVsiAppContext();

    const [firmwareData, setFirmwareData] = useState([]);

    useTitleFromComponent(`${searchParams.get('device_name')} / ${title}`);

    const getDiagram = async function () {
        const deviceName = searchParams.get('device_name');
        const route = `/api/v6/report/device_physical_diagram_proc?client_id=${client.clientId}&period_id=${periodId}&device_name=${deviceName}`;
        const token = await getAccessTokenSilently();
        try {
            const data = await getReport(route, null, token);
            const allData = data?.data?.recordset ?? [];
            const mungedDiagrams = formatPhysicalDiagramEnclosures(allData);
            setFirmwareData(allData ?? []);
            setActiveLoopName(mungedDiagrams?.[0]?.loop_name);
            setDiagrams(mungedDiagrams);
            setIsLoading(false);
        } catch (err) {
            console.error(err);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getDiagram();
    }, [searchParams]);

    const filterChanged = (eventData) => {
        let rowData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            rowData.push(node.data);
        });
        const mungedData = formatPhysicalDiagramEnclosures(rowData);

        setDiagrams(mungedData);
    };

    const handlePaginationClick = (e, loopIndex) => {
        e.preventDefault();
        if (loopIndex === "next") {
            if (activeLoop === diagrams.length - 1) {
                // setActiveLoop(0);
            } else {
                setActiveLoopName(diagrams?.[activeLoop + 1]?.loop_name);
                setActiveLoop(activeLoop + 1);
            }
        } else if (loopIndex === "previous") {
            if (activeLoop > 0) {
                setActiveLoopName(diagrams?.[activeLoop - 1]?.loop_name);
                setActiveLoop(activeLoop - 1);
            } else {
                // setActiveLoop(diagrams.length - 1);
            }
        } else {
            setActiveLoopName(diagrams?.[loopIndex]?.loop_name);
            setActiveLoop(loopIndex);
        }
    };

    return <Card className={`report-card p-2 nav-card-${theme}`}>
        <VsiH4>Physical Diagram - {searchParams.get('device_name')}</VsiH4>
        <VsiBubble noAutobot>
            <VsiMedLabel className={"mt-2"}>Legend</VsiMedLabel>
            <section className={"mb-2 d-flex flex-row"}>
                <LegendDiagram label={"Fibre"} theme={themeStyles[theme]}/>
                <LegendDiagram label={"SATA"} theme={themeStyles[theme]}/>
                <LegendDiagram label={"SAS"} theme={themeStyles[theme]}/>
                <LegendDiagram label={"SSD"} theme={themeStyles[theme]}/>
                <LegendDiagram label={"Unknown"} theme={themeStyles[theme]}/>
                <LegendDiagram label={"Empty"} theme={themeStyles[theme]}/>
                <LegendDiagram label={"Hot Spare"} theme={themeStyles[theme]}/>
                <LegendDiagram label={"Failed"} theme={themeStyles[theme]}/>

            </section>
        </VsiBubble>
        <VsiBubble className="mb-2" autobotName='DriveDiagrams' style={{
                height: 450,
                overflowY: 'scroll'
            }} autobotInfo={{data: diagrams}}>
            {isLoading ? <Spinner color={"success"}/> : null}
            {diagrams.length > 0 ? <VsiMedLabel>Select a loop</VsiMedLabel> : null}
            {diagrams.length > 0 ? <Pagination style={{marginBottom: 'unset'}}>
                <PaginationItem disabled={activeLoop === 0}>
                    <PaginationLink previous className={`bg-${theme}`}
                                    disabled={activeLoop === 0}
                                    onClick={e => handlePaginationClick(e, "previous")}/>
                </PaginationItem>
                {diagrams.map(({loop_name}, index) => (
                    <PaginationItem key={`pd-pagination-btn-${loop_name}-${index}`} active={index === activeLoop}>
                        <PaginationLink onClick={e => handlePaginationClick(e, index)} className={`bg-${theme}`}>
                            <VsiMedText>{loop_name}</VsiMedText>
                        </PaginationLink>
                    </PaginationItem>
                ))}
                <PaginationItem
                    disabled={activeLoop === diagrams.length - 1}>
                    <PaginationLink next className={`bg-${theme}`}
                                    disabled={activeLoop === diagrams.length - 1}
                                    onClick={e => handlePaginationClick(e, "next")}/>
                </PaginationItem>
            </Pagination> : null}
            {diagrams?.[activeLoop]?.enclosures?.map?.((enc, index) => (
              <div key={`enclosure-${enc.enclosure_uid}-${index}`}>
                    <hr/>
                    <div>
                        <VsiH5>{enc.enclosure_name}
                            {enc.enclosure_name !== enc.enclosure_uid ?
                                <VsiSmallText> ({enc.enclosure_uid})</VsiSmallText> : null}
                        </VsiH5>
                        <VsiMedText>{enc.enclosure_slots} slots</VsiMedText>
                        <Badge className={"ms-2"} color="info">Firmware: {enc?.slots?.[0]?.revision}</Badge>
                        <Badge className={"ms-2"} color="info">Speed: {enc?.slots?.[0]?.speed || 0}</Badge>
                        <Badge className={"ms-2"} color={enc?.hot_spare_count > 0 ? "warning" : "success"}>Hot Spares: {enc?.hot_spare_count || 0}</Badge>
                        <Badge className={"ms-2"} color={enc?.failed_drive_count > 0 ? "danger" : "success"}>Failed
                            Drives: {enc?.failed_drive_count || 0}</Badge>
                    </div>
                    {enc.slots.map((drive, sI) => {
                        return <DriveDiagram drive={drive}
                                             compId={`pd-drive-${enc.enclosure_uid}-${index}-${drive.disk_group}-${sI}-${drive.slot}`}
                                             key={`pd-drive-${enc.enclosure_uid}-${index}-${drive.disk_group}-${sI}-${drive.slot}`}/>
                    })}
                </div>
            ))}
        </VsiBubble>
        <VsiH4>Firmware Summary - {searchParams.get('device_name')}</VsiH4>
        <VsiBubble>
            <Grid data={[...firmwareData]?.filter(drive => drive.loop === activeLoopName)}
                  filterChanged={filterChanged}
                  columnDefs={firmwareDefs}
                  isLoading={isLoading}/>
        </VsiBubble>
    </Card>
}
